import { useEffect } from 'react';

import { useQuery } from '@apollo/client';

import type { BatchJobQueryQuery } from '@/apollo/graphql';
import { BATCH_JOB_QUERY } from '@/gql/queries/batch-action.gql';

export type BatchJob = NonNullable<BatchJobQueryQuery['batchJob']>;

// Poll cadence for job progress (STMN-3360).
export const BATCH_JOB_POLL_INTERVAL_MS = 2000;

// Terminal batch job statuses (batch_job status enum, backend batch.schema.ts).
// Once the job reaches one of these, polling stops.
export const TERMINAL_BATCH_JOB_STATUSES = ['DONE', 'FAILED', 'CANCELLED'];

export const isTerminalBatchJobStatus = (status?: string | null): boolean =>
  !!status && TERMINAL_BATCH_JOB_STATUSES.includes(status);

/**
 * Polls `batchJob(id)` every ~2s to drive the progress UI (STMN-3360). Polling
 * automatically stops once the job reaches a terminal status
 * (DONE/FAILED/CANCELLED). Passing a nullish `jobId` skips the query entirely.
 */
export function useBatchJob(jobId?: string | null) {
  const { data, loading, error, stopPolling } = useQuery(BATCH_JOB_QUERY, {
    variables: { id: jobId ?? '' },
    skip: !jobId,
    pollInterval: BATCH_JOB_POLL_INTERVAL_MS,
    // Re-opening a job you already looked at paints from cache while the poll
    // revalidates, instead of blanking the pane on every click.
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  const job = data?.batchJob ?? null;
  const isTerminal = isTerminalBatchJobStatus(job?.status);

  useEffect(() => {
    if (isTerminal) stopPolling();
  }, [isTerminal, stopPolling]);

  return { job, loading, error, isTerminal };
}
